import { useState, createContext, useEffect } from 'react'

import { useSelector, useDispatch } from 'react-redux'
import { isFunction } from 'lodash'

import ChatRooms from '../Components/shared/ChatRooms/ChatRooms'
import { selectUser } from '../reducers/UserReducer'
import { isObject } from '../utils'
import { selectTranslate } from '../reducers/LocaleReducer/LocalReducer'
import { addChatRoomToShift, getShiftChatRooms } from '../utils/api/shift'
import { CHAT_ROOM_TYPES, ChatRoom } from '../utils/entities/chatRoom'
import { HttpStatuses } from '../utils/http'
import { updateShiftPinnedChatMessage } from '../reducers/ShiftsReducer/ShiftsReducer'

export const ScheduleChatRoomsContext = createContext()

const ScheduleChatRooms = ({ children, onOpen }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)
  const dispatch = useDispatch()

  const [handler, setHandler] = useState({ shift: null, callback: null })
  const [chatRooms, setChatRooms] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (handler.shift && isObject(user)) {
      setLoading(true)

      getShiftChatRooms(user, handler.shift).then(({ data }) => {
        setChatRooms(data)
      }).finally(() => {
        setLoading(false)
      })
    } else {
      setChatRooms([])
    }
  }, [user?.id, handler.shift])

  useEffect(() => {
    if (isFunction(onOpen) && handler.shift) {
      onOpen(handler.shift)
    }
  }, [onOpen, handler.shift])

  const handleCreateChatRoom = () => {
    return addChatRoomToShift(user, handler.shift, new ChatRoom({ type: CHAT_ROOM_TYPES.SCHEDULE })).then(({ data }) => {
      setChatRooms(prevChatRooms => ([...prevChatRooms, data]))
      return data
    }).catch(error => {
      if (error.status === HttpStatuses.CONFLICT) {
        setHandler({ ...handler })
        error = { status: HttpStatuses.CONFLICT, type: 'RELOAD_REQUIRED' }
      } else {
        console.error('Error while creating chat room for shift:', error)
      }

      return Promise.reject(error)
    })
  }

  const handlePinnedMessageChange = pinnedChatMessage => {
    if (!handler.shift) {
      return
    }

    dispatch(updateShiftPinnedChatMessage(handler.shift, pinnedChatMessage))
  }

  const handleClose = () => {
    const { shift, callback } = handler

    setHandler({ shift: null, callback: null })

    if (isFunction(callback)) {
      // The rooms sitting in state were fetched before the messages were requested, and the backend
      // only records the consultation when they are, so replaying them would revive an unread state
      // the user has just cleared. Ask the server, which knows the truth by now.
      getShiftChatRooms(user, shift).then(({ data }) => callback(data)).catch(error => {
        console.error('Error while refreshing the shift chat rooms:', error)
      })
    }
  }

  return (
    <>
      <ScheduleChatRoomsContext.Provider value={{ setHandler }}>
        {children}
      </ScheduleChatRoomsContext.Provider>
      {handler?.shift && (
        <ChatRooms
          loading={loading}
          chatRooms={chatRooms}
          title={t('schedules_chat_rooms.title')}
          onClose={handleClose}
          onPinnedMessageChange={handlePinnedMessageChange}
          onChatRoomCreate={handleCreateChatRoom}
          onSendMessage
          onUnread
          onFind
        />
      )}
    </>
  )
}

export default ScheduleChatRooms
